import { createContext, useContext, useLayoutEffect, useRef, useState, type MouseEvent, type ReactNode, type Ref } from "react";
import { Link, useLocation, useNavigate, useNavigationType, type LinkProps } from "react-router";
import "./scene-controls.css";

const routes = new Set(["/home", "/map", "/memories", "/album", "/play", "/me", "/communicator", "/collection", "/timeline", "/guides", "/guides/wish", "/journey"]);
const channels = new Set(["private", "family", "friends", "phone", "petchats"]);
const identifier = /^[A-Za-z0-9_-]{1,128}$/;
const FIELD = "sceneEntry";
type Entry = { version: 1; parent: string; origin: string };
type Frame = { key: string; path: string; href: string | null; entry: Entry | null };

/** Navigation only. Never a pet identity, query key or permission. */
export function safeSceneHref(value: unknown): string | null {
  if (typeof value !== "string" || value.length > 1024 || !value.startsWith("/") || value.startsWith("//")
    || value.includes(String.fromCharCode(92)) || value.includes("#") || /%25/i.test(value)
    || [...value].some(c => c.charCodeAt(0) < 32 || c.charCodeAt(0) === 127)) return null;
  const rawPath = value.split("?", 1)[0];
  let guideDetail = false;
  const guideSegment = /^\/guides\/([^/]+)$/.exec(rawPath)?.[1];
  if (guideSegment) {
    try {
      const id = decodeURIComponent(guideSegment);
      guideDetail = /^[\p{L}\p{N}_-]{1,128}$/u.test(id) && id !== "wish" && id !== "plan";
    } catch { return null; }
  }
  if ((rawPath.includes("%") && !guideDetail) || rawPath.split("/").some(segment => segment === "." || segment === "..")) return null;
  try {
    const url = new URL(value, "https://scene.invalid");
    if (url.origin !== "https://scene.invalid" || (!routes.has(url.pathname) && !guideDetail)) return null;
    const params = new URLSearchParams();
    const keep = (key: string, allowed: (v: string) => boolean) => {
      const values = url.searchParams.getAll(key);
      if (values.length > 1 || (values.length === 1 && !allowed(values[0]))) throw new Error("invalid view");
      if (values.length) params.set(key, values[0]);
    };
    if (url.pathname === "/guides") keep("journey", v => identifier.test(v));
    if (url.pathname === "/collection") keep("source_event_id", v => identifier.test(v));
    if (url.pathname === "/home") keep("room", v => v === "inside" || v === "courtyard");
    if (url.pathname === "/map") keep("focus", v => identifier.test(v));
    if (url.pathname === "/communicator") {
      keep("channel", v => channels.has(v));
      if (params.get("channel") === "petchats") keep("thread", v => identifier.test(v));
    }
    return url.pathname + (params.size ? "?" + params.toString() : "");
  } catch { return null; }
}

const JOURNEY_FIELD = "sceneJourney";
type JourneyEntry = { version: 1; list: string; origin: string };
/** Bounded view trail only, never used by identity, readers or query keys. */
function readJourneyEntry(state: unknown): JourneyEntry | null {
  const descriptor=Object.getOwnPropertyDescriptor(objectState(state),JOURNEY_FIELD);
  if(!descriptor || !("value" in descriptor))return null;
  const fields=Object.getOwnPropertyDescriptors(objectState(descriptor.value));
  if(Object.values(fields).some(field => !("value" in field)))return null;
  const list=safeSceneHref(fields.list?.value), origin=safeSceneHref(fields.origin?.value);
  return fields.version?.value===1 && list?.startsWith("/guides?journey=") && origin && origin!==list ? {version:1,list,origin} : null;
}
function objectState(state: unknown): Record<string, unknown> {
  return state && typeof state === "object" && !Array.isArray(state) ? state as Record<string, unknown> : {};
}
export function readSceneEntry(state: unknown, current: string | null): Entry | null {
  const raw = objectState(objectState(state)[FIELD]);
  const parent = safeSceneHref(raw.parent), origin = safeSceneHref(raw.origin);
  return raw.version === 1 && parent && origin && parent !== current ? { version: 1, parent, origin } : null;
}
export function preserveSceneState(state: unknown, remove?: string): Record<string, unknown> {
  const next = { ...objectState(state) };
  if (remove) delete next[remove];
  return next;
}
function mergeEntry(state: unknown, entry: Entry | null) {
  const next = preserveSceneState(state);
  if (entry) next[FIELD] = entry; else delete next[FIELD];
  return next;
}
const plainClick = (event: MouseEvent<HTMLAnchorElement>) => !event.defaultPrevented && event.button === 0
  && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
type Navigation = { key: string | null; entry: Entry | null; returnFrom:string|null; rememberReturn:(target:string,owned:boolean)=>void; canPop: (target: string) => boolean };
type ReturnIntent = { key:string; from:string; to:string; owned:boolean; arrived?:string };
const NavigationContext = createContext<Navigation | null>(null);

/** Observe this tab's chain. Refresh starts with one frame; POP is never intercepted. */
export function SceneNavigationProvider({ children }: { children: ReactNode }) {
  const location = useLocation(), type = useNavigationType(), navigate = useNavigate();
  const frames = useRef<Frame[]>([]), index = useRef(0);
  const surface=useRef<HTMLDivElement>(null), returnIntent=useRef<ReturnIntent|null>(null);
  const intent = useRef<{ key: string; from: string; to: string } | null>(null);
  const [observed, setObserved] = useState<Navigation>({ key: null, entry: null, returnFrom:null, rememberReturn:()=>{}, canPop: () => false });
  const href = safeSceneHref(location.pathname + location.search);
  const supplied = readSceneEntry(location.state, href);
  useLayoutEffect(() => {
    const old = frames.current[index.current];
    if (old?.key === location.key) return;
    const returning=returnIntent.current;
    const returnedFrom=returning && returning.key===old?.key && returning.from===old.href && returning.to===href ? returning.from : null;
    if(returnedFrom && returning)returning.arrived=location.key; else returnIntent.current=null;
    let entry = readSceneEntry(location.state, href);
    const clicked = intent.current;
    if (type === "PUSH" && !entry && old && clicked?.key === old.key && clicked.from === old.href && clicked.to === href && old.href && href && href !== old.href) {
      entry = { version: 1, parent: old.href, origin: sceneOrigin(old.href, old.entry) };
    }
    // Existing game engines PUSH /play on their explicit exit. Reuse only the observed hub's origin.
    const previousHub = frames.current[index.current - 1];
    if (type === "PUSH" && !entry && href === "/play" && old && ["/play/snake", "/play/basket", "/play/flight"].includes(old.path) && previousHub?.href === "/play") entry = previousHub.entry;
    const frame = { key: location.key, path: location.pathname, href, entry };
    if (!old) { frames.current = [frame]; index.current = 0; }
    else if (type === "PUSH") {
      frames.current = [...frames.current.slice(0, index.current + 1), frame].slice(-32);
      index.current = frames.current.length - 1;
    } else if (type === "REPLACE") frames.current[index.current] = frame;
    else {
      const found = frames.current.findIndex(f => f.key === location.key);
      if (found < 0) { frames.current = [frame]; index.current = 0; }
      else { index.current = found; frames.current[found] = frame; }
    }
    intent.current = null;
    setObserved({ key: location.key, entry, returnFrom:returnedFrom, rememberReturn:()=>{}, canPop: target => frames.current[index.current]?.key === location.key
      && frames.current[index.current]?.entry?.parent === target
      && index.current > 0 && frames.current[index.current - 1]?.href === target });
    // Existing phone application Links keep their own handlers. Persist only a witnessed click entry.
    if (entry && !readSceneEntry(location.state, href) && type === "PUSH") {
      void navigate(location.pathname + location.search, { replace: true, state: mergeEntry(location.state, entry) });
    }
  }, [location.key, location.pathname, location.search, location.state, href, type, navigate]);
  // Restore only after an explicit owned back action, on the current visible
  // source link. Newer focus/pointer intent cancels delayed DOM completion.
  useLayoutEffect(() => {
    const request=returnIntent.current, root=surface.current;
    if(!request?.owned || request.arrived!==location.key || observed.key!==location.key || request.to!==href || !root)return;
    let stopped=false;
    const stop=()=>{stopped=true;observer.disconnect();if(returnIntent.current===request)returnIntent.current=null;};
    const visible=(node:HTMLElement)=>{
      if(node.closest('[hidden],[inert],[aria-hidden="true"]') || node.hasAttribute("disabled") || node.getAttribute("aria-disabled")==="true")return false;
      for(let current:HTMLElement|null=node;current && root.contains(current);current=current.parentElement){const style=getComputedStyle(current);if(style.display==="none" || style.visibility==="hidden")return false;}
      return true;
    };
    const restore=()=>{
      if(stopped || returnIntent.current!==request || !root.isConnected)return;
      const active=document.activeElement;
      if(active && active!==document.body && active.isConnected){stop();return;}
      const source=Array.from(root.querySelectorAll<HTMLAnchorElement>("a[href]")).find(link=>safeSceneHref(link.getAttribute("href"))===request.from && visible(link));
      if(source){stop();source.focus({preventScroll:true});return;}
      if(root.querySelector('[aria-busy="true"]'))return;
      const fallback=root.querySelector<HTMLElement>(".ps-page .ps-topbar h1");
      if(fallback && visible(fallback)){stop();fallback.tabIndex=-1;fallback.focus({preventScroll:true});}
    };
    const observer=new MutationObserver(restore);observer.observe(root,{subtree:true,childList:true,attributes:true});
    document.addEventListener("pointerdown",stop,true);
    const focusChanged=()=>{if(document.activeElement!==document.body)stop();};document.addEventListener("focusin",focusChanged,true);
    queueMicrotask(restore);
    return()=>{stop();document.removeEventListener("pointerdown",stop,true);document.removeEventListener("focusin",focusChanged,true);};
  },[location.key,href,observed.key]);
  return <NavigationContext.Provider value={{ ...observed, rememberReturn:(target,owned)=>{if(href && target!==href)returnIntent.current={key:location.key,from:href,to:target,owned};}, entry: supplied ?? (observed.key === location.key ? observed.entry : null) }}>
    <div ref={surface} className="ps-scene-navigation" onClickCapture={event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.defaultPrevented) return;
      const anchor = (event.target as Element).closest?.("a[href]");
      if (!anchor || anchor.hasAttribute("data-scene-link") || anchor.getAttribute("target") === "_blank" || anchor.hasAttribute("download") || !href) return;
      const to = safeSceneHref(anchor.getAttribute("href"));
      if (to && to !== href) intent.current = { key: location.key, from: href, to };
    }}>{children}</div>
  </NavigationContext.Provider>;
}

function useEntry() {
  const location = useLocation(), observed = useContext(NavigationContext);
  const current = safeSceneHref(location.pathname + location.search);
  return { location, current, entry: current === null && location.pathname.startsWith("/guides/") ? null : readSceneEntry(location.state, current) ?? (observed?.key === location.key ? observed.entry : null), observed };
}
function sceneOrigin(current: string, entry: Entry | null): string {
  return current.split("?", 1)[0] === "/home" || current.split("?", 1)[0] === "/map" ? current : entry?.origin ?? current;
}
export function useSceneReturnedHref() {
  const location=useLocation(), observed=useContext(NavigationContext);
  return observed?.key===location.key ? observed.returnFrom : null;
}
export function useSceneEntryState(to: string, state?: unknown) {
  const { location, current, entry } = useEntry();
  const target = safeSceneHref(to);
  const next=mergeEntry(state, current && target && current !== target ? { version: 1, parent: current, origin: sceneOrigin(current, entry) } : null);
  const travel=readJourneyEntry(location.state);
  if(current==="/journey" && target?.startsWith("/guides?journey="))next[JOURNEY_FIELD]={version:1,list:target,origin:sceneOrigin(current,entry)};
  else if(travel && (current===travel.list || current?.startsWith("/guides/")) && (target===travel.list || target?.startsWith("/guides/")))next[JOURNEY_FIELD]=travel;
  else delete next[JOURNEY_FIELD];
  return next;
}
export function SourceLink({ to, state, ...props }: LinkProps & { ref?: Ref<HTMLAnchorElement> }) {
  const target = typeof to === "string" ? to : null;
  const entryState = useSceneEntryState(target ?? "", state);
  return <Link {...props} to={to} state={target && safeSceneHref(target) ? entryState : state} data-scene-link="entry" />;
}
type BackProps = Omit<LinkProps, "to" | "state"> & { fallback: string; levelParent?: string; parentPath?: string; fallbackLabel?: string };
export function SourceBack({ fallback, levelParent, parentPath, fallbackLabel, onClick, ...props }: BackProps) {
  const { location, current, entry, observed } = useEntry();
  const navigate = useNavigate(), travel=readJourneyEntry(location.state);
  const parent = entry && (!parentPath || entry.parent.split("?", 1)[0] === parentPath) ? entry.parent : null;
  const target = safeSceneHref(levelParent) ?? parent ?? safeSceneHref(fallback) ?? "/map";
  const safeTarget = target === current ? (current?.split("?", 1)[0] === "/map" ? "/home" : "/map") : target;
  let nextEntry: Entry | null = null;
  if (entry && entry.origin !== safeTarget) {
    const parentOfLevel = safeTarget.startsWith("/communicator?channel=petchats") ? "/communicator?channel=phone" : entry.origin;
    nextEntry = { version: 1, parent: parentOfLevel, origin: entry.origin };
  }
  if(travel && safeTarget===travel.list) nextEntry={version:1,parent:"/journey",origin:travel.origin};
  const label = safeTarget.startsWith("/home") ? "回到小屋" : safeTarget.startsWith("/map") ? "返回地图"
    : safeTarget.startsWith("/journey") ? "返回旅途" : safeTarget.startsWith("/guides") ? "返回手账列表" : safeTarget === "/album" ? "返回相册" : safeTarget === "/memories" ? "返回回忆" : safeTarget === "/play" ? "返回小游戏"
      : safeTarget.startsWith("/communicator?channel=petchats") ? "返回会话列表" : safeTarget.startsWith("/communicator") ? "返回手机" : "返回";
  return <Link {...props} aria-label={props["aria-label"] ?? (!parent && !levelParent && fallbackLabel ? fallbackLabel : label)} to={safeTarget} replace state={mergeEntry(location.state, nextEntry)} data-scene-link="back"
    onClick={event => {
      onClick?.(event);
      if (!plainClick(event)) return;
      event.preventDefault();
      observed?.rememberReturn(safeTarget,event.currentTarget.contains(document.activeElement));
      if (observed?.canPop(safeTarget)) void navigate(-1);
      else void navigate(safeTarget, { replace: true, state: mergeEntry(location.state, nextEntry) });
    }} />;
}
